import { pacificToday } from '../../../shared/dates';
import type { IsoDate } from '../../../shared/types';

/** Local fields of a civil Date; use pacificToday for timestamp calendar days. */
export const toIsoDate = (d: Date): IsoDate => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const todayLocal = (): IsoDate => pacificToday(new Date());

/** Parse YYYY-MM-DD as a LOCAL date (never `new Date('YYYY-MM-DD')`, which is UTC). */
export const parseIsoDate = (iso: IsoDate): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

export const addDays = (iso: IsoDate, days: number): IsoDate => {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
};

/** Whole calendar days from `from` to `to` (positive when `to` is later). DST-safe. */
export const diffDays = (to: IsoDate, from: IsoDate): number => {
  const a = parseIsoDate(to);
  const b = parseIsoDate(from);
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcA - utcB) / 86_400_000);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Aug 19" (adds the year when it is not the current one). */
export const formatDate = (iso: IsoDate, today: IsoDate = todayLocal()): string => {
  const d = parseIsoDate(iso);
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === parseIsoDate(today).getFullYear() ? base : `${base}, ${d.getFullYear()}`;
};

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Saturday · Oct 10" — the Home and Today headers. */
export const dayHeading = (today: IsoDate): string =>
  `${WEEKDAYS_LONG[parseIsoDate(today).getDay()]} · ${formatDate(today, today)}`;

/** "Today" / "Tomorrow" / "Yesterday" / "Tue, Aug 26" / "3 days ago" / "in 12 days" */
export const relativeDay = (iso: IsoDate, today: IsoDate = todayLocal()): string => {
  const diff = diffDays(iso, today);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < 0) return `${-diff} days ago`;
  if (diff <= 6) return `${WEEKDAYS[parseIsoDate(iso).getDay()]}, ${formatDate(iso, today)}`;
  return `in ${diff} days`;
};

/** "Aug 19, 3:04 PM" from an ISO timestamp, in local time. */
export const formatDateTime = (isoDateTime: string, today: IsoDate = todayLocal()): string => {
  const d = new Date(isoDateTime);
  if (Number.isNaN(d.getTime())) return isoDateTime;
  const h24 = d.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ampm = h24 < 12 ? 'AM' : 'PM';
  return `${formatDate(toIsoDate(d), today)}, ${h}:${mm} ${ampm}`;
};

const pacificClock = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', hour12: true,
});

/** "Nov 23, 10:30 AM" in California whatever the device timezone (the year when it is not this
 *  one). Built from parts so ICU's narrow no-break space before AM/PM never reaches the screen. */
export const pacificDateTime = (isoDateTime: string, now: Date = new Date()): string => {
  const d = new Date(isoDateTime);
  if (Number.isNaN(d.getTime())) return isoDateTime;
  const p: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of pacificClock.formatToParts(d)) p[part.type] = part.value;
  const year = p.year === pacificToday(now).slice(0, 4) ? '' : `, ${p.year}`;
  return `${p.month} ${p.day}${year}, ${p.hour}:${p.minute} ${p.dayPeriod?.toUpperCase()}`;
};

/** "3d ago" / "today" / "2w ago" — compact, for list rows. */
export const timeAgo = (isoDateTime: string, now: Date = new Date()): string => {
  const then = new Date(isoDateTime).getTime();
  if (Number.isNaN(then)) return '';
  const days = diffDays(pacificToday(now), pacificToday(new Date(then)));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

/** "just now" / "3m ago" / "2h ago", then falls back to timeAgo for older timestamps. */
export const timeAgoShort = (isoDateTime: string, now: Date = new Date()): string => {
  const then = new Date(isoDateTime).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((now.getTime() - then) / 1000));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  return timeAgo(isoDateTime, now);
};

/** "14:30" → "2:30 PM"; anything unparseable comes back as-is. */
export const formatTime = (hhmm: string): string => {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) return hhmm;
  const h24 = Number(m[1]);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${m[2]} ${h24 < 12 ? 'AM' : 'PM'}`;
};
